import React from "react";
import { styled } from "@mui/system";
import Profileicon from "src/icons/Profileicon";
import { AccountUsersRes } from "src/store/features/api/generated";
import Tick from "src/icons/Tick";
import BalanceTooltip from "../wallets/header/BalanceTooltip";

const Wrapper = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  cursor: "pointer",
  width: "100%",

  [theme.breakpoints.down(900)]: {
    width: "100%",
  },
}));

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  width: "100%",
  padding: "4px 8px",
  alignItems: "flex-start",
  gap: "12px",
  background: "#FFF",
  "&:hover": {
    background: "#F8F9F9",
  },

  [theme.breakpoints.down(900)]: {
    width: "100%",
  },
}));

const IconWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "flex-start",
}));

const IconCricle = styled("div")(({ theme }) => ({
  display: "flex",
  padding: "8px",
  justifyContent: "center",
  alignItems: "center",
  gap: "4px",
  borderRadius: "20px",
  background: theme.palette.secondary.contrastText,
}));

const ContentWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  gap: "4px",
  flex: "1 0 0",
}));

interface TextProps {
  bold?: boolean;
}
const Text = styled("div")<TextProps>(({ bold }) => ({
  alignSelf: "stretch",
  color: "#002035",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: bold ? 600 : 400,
  lineHeight: "17px",
}));

const DefaultWrapper = styled("div")(() => ({
  display: "flex",
  padding: "4px 8px",
  justifyContent: "center",
  alignItems: "right",
  gap: "8px",
}));

interface DefaultTextProps {
  show: boolean;
}
const DefaultText = styled("div")<DefaultTextProps>(({ show }) => ({
  color: "#002035",
  fontFamily: "Söhne",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "14px",
  visibility: show ? "visible" : "hidden",
}));

interface MultiAccountItemProps {
  accountName: string;
  customerID: string;
  isDefault?: boolean;
  isUnverified?: boolean;
  selected: boolean;
  onClick?: (account: AccountUsersRes) => void;
}

/**
 * A component representing an item in a multi-account list.
 * @param {string} props.accountName - The name of the account.
 * @param {string} props.customerID - The customer ID associated with the account.
 * @param {boolean} [props.isDefault=false] - Whether the account is the default one (default is false).
 * @param {boolean} [props.isUnverified=false] - Whether the account is unverified (default is false).
 * @param {Function} [props.onClick] - The function to run when user clicks on a NotificationItem (optional).
 */
const MultiAccountItem = (props: MultiAccountItemProps) => {
  const { accountName, customerID, isDefault, isUnverified, selected, onClick } = props;

  const handleOnClick = () => {
    if (onClick) {
      onClick({
        name: accountName,
        customerId: customerID,
        default: isDefault ?? false,
        isUnverified: isUnverified ?? false,
        selected: true,
      });
    }
  };

  const multiAccountDisclaimer =
    "Our Onboarding team is processing your account now, please standby as we do this. We will notify you when your account has been successfully onboarded.";

  return (
    <BalanceTooltip message={multiAccountDisclaimer} align="top" show={false} ignoreGrid contrast>
      <Wrapper onClick={handleOnClick}>
        <Row>
          {/* Icon */}
          <IconWrapper>
            <IconCricle>
              <Profileicon size="16" />
            </IconCricle>
          </IconWrapper>

          {/* Account info */}
          <ContentWrapper>
            <Text bold>
              {accountName}
              {selected && (
                <span style={{ paddingLeft: "8px" }}>
                  <Tick size="13px" color="#0BEC9F" tickStroke="2" />
                </span>
              )}
            </Text>
            <Text>{customerID}</Text>
          </ContentWrapper>

          {/* Default ? */}
          <DefaultWrapper>
            {!!isDefault ? (
              <DefaultText show={true}>{"Default"}</DefaultText>
            ) : (
              <DefaultText show={!!isUnverified}>{"Pending"}</DefaultText>
            )}
          </DefaultWrapper>
        </Row>
      </Wrapper>
    </BalanceTooltip>
  );
};

export default MultiAccountItem;
